import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'semantic-ui-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import axiosInstance from '../../lib/auth/axiosApi';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../lib/auth/useAuth';
import './StoreModal.css';

const isDev = process.env.NODE_ENV === 'development';
const isDocker = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const publicApi = axios.create({
  baseURL: isDev
    ? `http://${isDocker ? 'dijon-backend' : 'localhost'}:8019/dijon-backend/api/`
    : `https://dijondreams.com/dijon-backend/api/`,
  headers: { 'Content-Type': 'application/json' }
});

// Filter the shelf by mustard style / origin, derived from each product.
const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'classic', label: 'Classic Dijon', test: p => /dijon/i.test(p.name) && !/grain|basil|blackcurrant|tarragon|honey|hot|strong/i.test(p.name) },
  { key: 'grain', label: 'Whole-grain', test: p => /grain/i.test(p.name) },
  { key: 'hot', label: 'Hot & spicy', test: p => /hot|extra strong|\bstrong\b/i.test(p.name) },
  { key: 'flavored', label: 'Flavored', test: p => /basil|blackcurrant|tarragon|honey|herb|garlic|fig|truffle/i.test(p.name) },
  { key: 'french', label: 'French', test: p => p.origin_country === 'France' },
  { key: 'german', label: 'German', test: p => p.origin_country === 'Germany' },
];

// Where each maison is, for the origin map. Keyed by producer name.
const ORIGINS = {
  'Edmond Fallot': { city: 'Beaune', region: 'Burgundy', country: 'France', lat: 47.0257, lng: 4.8398 },
  'Amora': { city: 'Dijon', region: 'Burgundy', country: 'France', lat: 47.3220, lng: 5.0415 },
  'Maille': { city: 'Dijon', region: 'Burgundy', country: 'France', lat: 47.3105, lng: 5.0180 },
  'Domaine des Vignes': { city: 'Dijon', region: 'Burgundy', country: 'France', lat: 47.2650, lng: 5.1250 },
  'Lowensenf': { city: 'Düsseldorf', region: 'North Rhine-Westphalia', country: 'Germany', lat: 51.2277, lng: 6.7735 },
  'Handlmaier': { city: 'Regensburg', region: 'Bavaria', country: 'Germany', lat: 49.0134, lng: 12.1016 },
  'Kuhne': { city: 'Hamburg', region: '', country: 'Germany', lat: 53.5511, lng: 9.9937 },
};

const pinIcon = L.divIcon({
  className: 'mstore-pin',
  html: '<span></span>',
  iconSize: [18, 18], iconAnchor: [9, 18], popupAnchor: [0, -16],
});

// Real, attributed quotes gathered from around the web (Reddit r/Mustard,
// Sporked, TODAY, The Ambrosian Pantry, retailer reviews). Never fabricate these.
const REVIEWS = [
  { quote: "If you're looking for the punchiest, zestiest, best dijon on the market, Maille is the best dijon mustard brand, hands down.", author: "Sporked", url: "https://sporked.com/article/best-dijon-mustard/", brand: "Maille" },
  { quote: "Maille on tap at the store in Paris. Unreal stuff.", author: "u/liketosaysalsa · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Maille" },
  { quote: "The answer here, as many have suggested, is Maille. I order three-packs, since I eat it like it's candy.", author: "u/SVLibertine · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Maille" },
  { quote: "Amora is my favorite. Apparently it's the original Dijon. Maille is a close second.", author: "u/RhythmicJerk · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Amora" },
  { quote: "I've been told (by French people) that Amora is the best. I like Maille also, and the Trader Joe's Dijon.", author: "u/Any-Doubt-5281 · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Amora" },
  { quote: "It is definitely stronger than Maille. In fact, it is perhaps better described as très, très forte.", author: "The Mustard Report", url: "https://www.elin.ca/the-mustard-report-amora-dijon/", brand: "Amora" },
  { quote: "Hats off to Edmond Fallot — tangy, spicy, smooth and surprisingly creamy. The one I couldn't stop spreading.", author: "Joey Skladany · TODAY", url: "https://www.today.com/food/groceries/best-dijon-mustard-rcna132255", brand: "Edmond Fallot" },
  { quote: "Those antique millstones are really onto something — a dreamy creaminess that makes it the best dijon for cooking.", author: "Sporked", url: "https://sporked.com/article/best-dijon-mustard/", brand: "Edmond Fallot" },
  { quote: "Edmond Fallot happens to be my everyday Dijon. I'm a fan.", author: "u/Dmnkly · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Edmond Fallot" },
  { quote: "Edmond Fallot is like a champagne, and French's yellow is like cheap American sparkling.", author: "u/Loveroffinerthings · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Edmond Fallot" },
  { quote: "Its classic Dijon is the refrigerator workhorse — smooth and assertive — and its seed-style is the whole-grain counterpart.", author: "The Ambrosian Pantry", url: "https://theambrosianpantry.com/blogs/the-pantry-dispatch/dijon-mustard-the-complete-guide", brand: "Edmond Fallot" },
  { quote: "The Trader Joe's Dijon, made in France, is awesome and an incredible deal. The only one I liked more was from Fauchon.", author: "u/JeanVicquemare · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "Trader Joe's" },
  { quote: "This mustard is so good, but you should buy two jars because it will go fast.", author: "The Taste of Germany review", url: "https://www.thetasteofgermany.com/lowensenf-medium-hot-duesseldorf-mustard-8-4-oz/", brand: "Löwensenf" },
  { quote: "Edmond Fallot for flavored Dijon, Maille for grainy, Amora for basic ground Dijon.", author: "u/nostrumest · r/Mustard", url: "https://www.reddit.com/r/Mustard/comments/1m1ssln/what_is_the_best_dijon_mustard_in_the_world/", brand: "general" },
];

const isBox = (p) => p.kind === 'box';
const isAvailable = (p) => (p.available === undefined ? p.in_stock : p.available);
const GIFT_MAX = 500;

const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const reviewsFor = (producer) => REVIEWS.filter(r => norm(r.brand) === norm(producer));
// Curated strip for the shop landing — leads with brands we carry.
const FEATURED_REVIEWS = [13, 6, 3, 12, 9, 1].map(i => REVIEWS[i]);

const Stars = ({ rating, count, showNum = true }) => {
  const r = Number(rating) || 0;
  const full = Math.round(r);
  return (
    <span className="mstore-stars">
      <span className="mstore-stars-glyphs">{'★'.repeat(full)}{'☆'.repeat(Math.max(0, 5 - full))}</span>
      {showNum && r > 0 && <span className="mstore-rating-num">{r.toFixed(1)}</span>}
      {count >= 10 && <span className="mstore-rating-count">({Number(count).toLocaleString()})</span>}
    </span>
  );
};

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

function StoreModal({ open, onClose }) {
  const [view, setView] = useState('browse'); // browse, product, cart, checkout, orders
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [quantity, setQuantity] = useState(1);
  const [shipping, setShipping] = useState({
    name: '', address_line1: '', address_line2: '',
    city: '', state: '', zip: '', country: 'US'
  });
  const [checkoutError, setCheckoutError] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [waitlist, setWaitlist] = useState({ email: '', zip: '', status: '' });

  const { cart, fetchCart, addToCart, updateCartItem, removeCartItem, checkout } = useCart();
  const auth = useAuth();

  useEffect(() => {
    if (open) {
      fetchProducts();
      if (auth.user) fetchCart();
    }
  }, [open]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = 'store/products/';
      if (search) url += `?search=${search}`;
      const res = await publicApi.get(url);
      setProducts(res.data.results || res.data);
    } catch (e) {
      console.error('Failed to fetch products:', e);
    }
    setLoading(false);
  };

  const fetchOrders = async () => {
    try {
      const res = await axiosInstance.get('store/orders/');
      setOrders(res.data.results || res.data);
    } catch (e) {
      console.error('Failed to fetch orders:', e);
    }
  };

  const openProduct = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setView('product');
  };

  const handleCheckout = async () => {
    setCheckoutError('');
    if (!shipping.name || !shipping.address_line1 || !shipping.city || !shipping.state || !shipping.zip) {
      setCheckoutError('Please fill in all required fields.');
      return;
    }
    await checkout(shipping, giftMessage);
  };

  const joinWaitlist = async () => {
    setWaitlist(w => ({ ...w, status: 'sending' }));
    try {
      const res = await publicApi.post('store/waitlist/', { email: waitlist.email, zip: waitlist.zip });
      setWaitlist(w => ({ ...w, status: res.data.already_on_list ? 'already' : 'joined' }));
    } catch (e) {
      setWaitlist(w => ({ ...w, status: e?.response?.data?.error || 'Something went wrong — try again.' }));
    }
  };

  const handleAdd = async (p, qty) => {
    if (await addToCart(p.id, qty)) { setView('cart'); fetchCart(); }
  };

  const stateOptions = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC'
  ].map(s => ({ key: s, text: s, value: s }));

  // ---------------- views ----------------

  const renderBrowse = () => {
    const active = FILTERS.find(f => f.key === category) || FILTERS[0];
    const boxes = products.filter(isBox);
    const filtered = products.filter(p => !isBox(p)).filter(active.test);
    const card = (p) => (
      <div key={p.id} className="mstore-card" onClick={() => openProduct(p)}>
        <div className="mstore-card-img">
          {p.image_url
            ? <img src={p.image_url} alt={p.name} />
            : <span style={{ fontSize: 34 }}>{isBox(p) ? '📦' : '🫙'}</span>}
          {!isAvailable(p) && <span className="soldout">Sold out</span>}
        </div>
        <div className="mstore-card-body">
          <div className="mstore-card-producer">{isBox(p) ? `${p.components.length} jars · shipping included` : p.producer}</div>
          <div className="mstore-card-name">{p.name}</div>
          <div className="mstore-card-foot">
            <span className="mstore-price">${p.price}</span>
            {!isBox(p) && <Stars rating={p.rating} count={p.rating_count} showNum={false} />}
          </div>
        </div>
      </div>
    );
    return (
      <>
        {!loading && boxes.length > 0 && !search && (
          <>
            <h2 className="mstore-title">Boxes</h2>
            <p className="mstore-sub">Curated sets of three or four jars. Shipping's included.</p>
            <div className="mstore-grid mstore-grid--shop">{boxes.map(card)}</div>

            <div className="mstore-waitlist">
              <div>
                <div className="mstore-waitlist-head">Mustard of the Month</div>
                <div className="mstore-waitlist-sub">3 jars, $39/mo, shipping included. Join the waitlist and we'll email you when it opens.</div>
              </div>
              {waitlist.status === 'joined' || waitlist.status === 'already' ? (
                <div className="mstore-waitlist-done">{waitlist.status === 'joined' ? "You're on the list!" : "You're already on the list."}</div>
              ) : (
                <div className="mstore-waitlist-form">
                  <input placeholder="Email" type="email" value={waitlist.email}
                    onChange={(e) => setWaitlist(w => ({ ...w, email: e.target.value, status: '' }))} />
                  <input placeholder="Zip" value={waitlist.zip} style={{ width: 90 }}
                    onChange={(e) => setWaitlist(w => ({ ...w, zip: e.target.value, status: '' }))} />
                  <button className="mbtn mbtn--dark" disabled={waitlist.status === 'sending'} onClick={joinWaitlist}>Join</button>
                  {waitlist.status && waitlist.status !== 'sending' && <div className="mstore-waitlist-err">{waitlist.status}</div>}
                </div>
              )}
            </div>

            <h2 className="mstore-title" style={{ marginTop: 34 }}>Add-on jars</h2>
            <p className="mstore-sub">Add any jar to a box order.</p>
          </>
        )}
        <div className="mstore-tools">
          <div className="mstore-cats">
            {FILTERS.map(c => (
              <button key={c.key} className={category === c.key ? 'active' : ''} onClick={() => setCategory(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="mstore-search">
            <SearchIcon />
            <input
              placeholder="Search mustards…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchProducts()}
            />
          </div>
        </div>

        {loading ? (
          <div className="mstore-loading">Loading the pantry…</div>
        ) : filtered.length === 0 ? (
          <div className="mstore-loading">No mustards found.</div>
        ) : (
          <div className="mstore-grid mstore-grid--shop">{filtered.map(card)}</div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mstore-social">
            <div className="mstore-social-head">Loved by mustard obsessives</div>
            <div className="mstore-social-grid">
              {FEATURED_REVIEWS.filter(Boolean).map((r, i) => (
                <blockquote key={i} className="mstore-quote">
                  <p>“{r.quote}”</p>
                  <cite>— <a href={r.url} target="_blank" rel="noreferrer">{r.author}</a></cite>
                </blockquote>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderProduct = () => {
    if (!selectedProduct) return null;
    const p = selectedProduct;
    const available = isAvailable(p);
    const needsBox = !isBox(p) && !cart.has_box;
    return (
      <>
        <div className="mstore-crumb">
          <button onClick={() => setView('browse')}>Shop</button>&nbsp;/&nbsp;{p.name}
        </div>
        <div className="mstore-pd">
          <div className="mstore-pd-img">
            {p.image_url ? <img src={p.image_url} alt={p.name} /> : <span style={{ fontSize: 64 }}>🫙</span>}
          </div>
          <div className="mstore-pd-info">
            <div className="origin">
              {isBox(p) ? 'Box · shipping included' : <>
                {p.producer}
                {p.origin_region && ` · ${p.origin_region}`}
                {p.origin_country && `, ${p.origin_country}`}
              </>}
            </div>
            <h2>{p.name}</h2>
            {!isBox(p) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Stars rating={p.rating} count={p.rating_count} />
                {p.rating_count >= 10 && <span style={{ color: 'var(--muted)', fontSize: 13 }}>{Number(p.rating_count).toLocaleString()} ratings</span>}
              </div>
            )}
            <div className="price-row">
              <span className="p">${p.price}</span>
              <span className={`mstore-pill ${available ? 'in' : 'out'}`}>{available ? 'In stock' : 'Out of stock'}</span>
            </div>
            <hr className="mstore-rule" />
            <p className="desc">{p.description}</p>
            {isBox(p) && (
              <div className="mstore-inside">
                <div className="mstore-inside-head">What's inside</div>
                {p.components.map(c => (
                  <div key={c.slug} className="mstore-inside-row">
                    {c.quantity > 1 ? `${c.quantity}× ` : ''}{c.label}
                    {!c.in_stock && <span className="mstore-inside-out"> · out of stock</span>}
                  </div>
                ))}
              </div>
            )}
            {!isBox(p) && <p className="mstore-addon-note">Add-on jar: add it to any box order.</p>}
            {reviewsFor(p.producer).length > 0 && (
              <div className="mstore-quotes">
                {reviewsFor(p.producer).slice(0, 3).map((r, i) => (
                  <blockquote key={i} className="mstore-quote">
                    <p>“{r.quote}”</p>
                    <cite>— <a href={r.url} target="_blank" rel="noreferrer">{r.author}</a></cite>
                  </blockquote>
                ))}
              </div>
            )}
            {auth.user && available && needsBox && (
              <p className="mstore-addon-note">Add a box to your cart first, then you can add this jar.</p>
            )}
            {auth.user && available && !needsBox && (
              <div className="mstore-buy">
                <div className="mstore-qty">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                  <span>{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)}>+</button>
                </div>
                <button className="mbtn mbtn--dark mbtn--lg" onClick={() => handleAdd(p, quantity)}>
                  Add to cart
                </button>
              </div>
            )}
            {!auth.user && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Sign in to add to your cart.</p>}
          </div>
        </div>
      </>
    );
  };

  const renderCart = () => {
    if (!cart.items || cart.items.length === 0) {
      return (
        <div className="mstore-empty">
          <div className="big">Your cart is empty</div>
          <button className="mbtn mbtn--dark" onClick={() => setView('browse')}>Shop mustards</button>
        </div>
      );
    }
    return (
      <>
        <h2 className="mstore-title">Your cart</h2>
        <table className="mstore-tbl">
          <thead><tr><th>Product</th><th>Price</th><th>Qty</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {cart.items.map(item => (
              <tr key={item.id}>
                <td>
                  <div className="pname">{item.product.name}</div>
                  <div className="pprod">{isBox(item.product) ? item.product.components.map(c => c.label).join(' · ') : `${item.product.producer} · add-on`}</div>
                </td>
                <td>${item.product.price}</td>
                <td>
                  <div className="mstore-qty" style={{ transform: 'scale(.85)', transformOrigin: 'left' }}>
                    <button onClick={() => updateCartItem(item.id, item.quantity - 1)}>−</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateCartItem(item.id, item.quantity + 1)}>+</button>
                  </div>
                </td>
                <td><strong>${item.line_total}</strong></td>
                <td><button className="mstore-linkbtn" onClick={() => removeCartItem(item.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 26 }}>
          <div className="mstore-summary" style={{ width: 320 }}>
            <div className="row"><span>Subtotal</span><span>${cart.subtotal}</span></div>
            <div className="row"><span>Shipping</span><span>{cart.shipping_cost === '0.00' ? 'Free' : `$${cart.shipping_cost}`}</span></div>
            <hr className="mstore-rule" style={{ margin: '14px 0' }} />
            <div className="total"><span>Total</span><span>${cart.total}</span></div>
            {!cart.has_box && <div className="mstore-addon-note" style={{ marginTop: 12 }}>Single jars are add-ons. Add a box to check out.</div>}
            <button className="mbtn mbtn--dark mbtn--full mbtn--lg" style={{ marginTop: 18 }} disabled={!cart.has_box} onClick={() => setView('checkout')}>Checkout</button>
            <button className="mbtn mbtn--ghost mbtn--full" style={{ marginTop: 8 }} onClick={() => setView('browse')}>Continue shopping</button>
          </div>
        </div>
      </>
    );
  };

  const renderCheckout = () => (
    <>
      <h2 className="mstore-title">Checkout</h2>
      <div className="mstore-checkout">
        <div>
          <Form>
            <Form.Input label="FULL NAME" value={shipping.name} onChange={(e, { value }) => setShipping(s => ({ ...s, name: value }))} />
            <Form.Input label="ADDRESS" value={shipping.address_line1} onChange={(e, { value }) => setShipping(s => ({ ...s, address_line1: value }))} />
            <Form.Input label="APT / SUITE (OPTIONAL)" value={shipping.address_line2} onChange={(e, { value }) => setShipping(s => ({ ...s, address_line2: value }))} />
            <Form.Group unstackable>
              <Form.Input label="CITY" width={7} value={shipping.city} onChange={(e, { value }) => setShipping(s => ({ ...s, city: value }))} />
              <Form.Select label="STATE" width={5} options={stateOptions} value={shipping.state} onChange={(e, { value }) => setShipping(s => ({ ...s, state: value }))} search />
              <Form.Input label="ZIP" width={4} value={shipping.zip} onChange={(e, { value }) => setShipping(s => ({ ...s, zip: value }))} />
            </Form.Group>
            <p className="mstore-addon-note">Sending it as a gift? Put their name and address above.</p>
            <Form.TextArea label="GIFT NOTE (OPTIONAL)" rows={3} maxLength={GIFT_MAX}
              placeholder="It goes on the tasting card in the box."
              value={giftMessage} onChange={(e, { value }) => setGiftMessage(value)} />
          </Form>
        </div>
        <div className="mstore-summary">
          <div style={{ fontFamily: 'var(--serif)', fontWeight: 600, fontSize: 16, marginBottom: 16 }}>Order summary</div>
          {cart.items && cart.items.map(item => (
            <div key={item.id} className="row"><span>{item.product.name} × {item.quantity}</span><span>${item.line_total}</span></div>
          ))}
          <hr className="mstore-rule" style={{ margin: '12px 0' }} />
          <div className="row"><span>Subtotal</span><span>${cart.subtotal}</span></div>
          <div className="row"><span>Shipping</span><span>{cart.shipping_cost === '0.00' ? 'Free' : `$${cart.shipping_cost}`}</span></div>
          <hr className="mstore-rule" style={{ margin: '12px 0' }} />
          <div className="total"><span>Total</span><span>${cart.total}</span></div>
          {checkoutError && <div style={{ color: '#a23a3a', fontSize: 13, marginTop: 12 }}>{checkoutError}</div>}
          <button className="mbtn mbtn--dark mbtn--full mbtn--lg" style={{ marginTop: 18 }} onClick={handleCheckout}>Pay securely with Stripe</button>
          <button className="mbtn mbtn--ghost mbtn--full" style={{ marginTop: 8 }} onClick={() => setView('cart')}>Back to cart</button>
        </div>
      </div>
    </>
  );

  const renderOrders = () => (
    <>
      <h2 className="mstore-title">Order history</h2>
      {orders.length === 0 ? (
        <div className="mstore-loading">No orders yet.</div>
      ) : (
        <table className="mstore-tbl">
          <thead><tr><th>Order</th><th>Date</th><th>Items</th><th>Total</th><th>Status</th></tr></thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td><strong>{order.order_number}</strong></td>
                <td>{new Date(order.created_at).toLocaleDateString()}</td>
                <td>{order.items.map(i => <div key={i.id} style={{ fontSize: 13 }}>{i.quantity}× {i.product_name}{i.contents ? <div className="pprod">{i.contents}</div> : null}</div>)}</td>
                <td><strong>${order.total}</strong></td>
                <td><span className="mstore-status" style={{
                  background: order.status === 'paid' ? '#eaf3ea' : '#f3efe6',
                  color: order.status === 'paid' ? '#2f6b3a' : 'var(--ink-soft)'
                }}>{order.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const renderOrigins = () => {
    const byOrigin = {};
    products.forEach(p => {
      const o = ORIGINS[p.producer];
      if (!o) return;
      const k = `${o.lat},${o.lng}`;
      if (!byOrigin[k]) byOrigin[k] = { o, items: [] };
      byOrigin[k].items.push(p);
    });
    const groups = Object.values(byOrigin);
    return (
      <>
        <h2 className="mstore-title">Where your mustard comes from</h2>
        <p style={{ color: 'var(--ink-soft)', fontSize: 14.5, marginTop: -8, marginBottom: 18, maxWidth: 640, lineHeight: 1.65 }}>
          Every jar traces back to a real maison — the Fallot mill in Beaune, the streets of Dijon,
          and a few German houses that have been grinding seed for over a century. Tap a pin to shop that spot.
        </p>
        <div className="mstore-map">
          <MapContainer center={[48.7, 6.2]} zoom={5} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; OpenStreetMap &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            {groups.map(g => (
              <Marker key={`${g.o.lat},${g.o.lng}`} position={[g.o.lat, g.o.lng]} icon={pinIcon}>
                <Popup>
                  <div style={{ fontFamily: 'var(--serif)', fontWeight: 600, fontSize: 15 }}>
                    {g.o.city}{g.o.region ? `, ${g.o.region}` : ''}
                  </div>
                  <div style={{ fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#97907f', margin: '2px 0 8px' }}>
                    {g.o.country}
                  </div>
                  {g.items.map(p => (
                    <div key={p.id} onClick={() => openProduct(p)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 16, padding: '5px 0', borderTop: '1px solid #efe9dd' }}>
                      <span>{p.name}</span><span style={{ fontWeight: 600 }}>${p.price}</span>
                    </div>
                  ))}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose} closeIcon className="mstore-modal" style={{ width: '95vw', maxWidth: '1180px' }}>
      <Modal.Content scrolling style={{ maxHeight: '90vh', height: '90vh', padding: 0 }}>
        <div className="mstore">
          <div className="mstore-hero">
            <div className="mstore-brand">
              <span className="kicker">Est. Rhode Island</span>
              <span className="wordmark">Dijon Dreams</span>
            </div>
            <div className="mstore-nav">
              <button className={view === 'browse' || view === 'product' ? 'active' : ''} onClick={() => setView('browse')}>Shop</button>
              <button className={view === 'origins' ? 'active' : ''} onClick={() => setView('origins')}>Origins</button>
              {auth.user && (
                <>
                  <button className={view === 'cart' || view === 'checkout' ? 'active' : ''} onClick={() => { setView('cart'); fetchCart(); }}>
                    Cart{cart.item_count > 0 && <span className="cart-dot">{cart.item_count}</span>}
                  </button>
                  <button className={view === 'orders' ? 'active' : ''} onClick={() => { setView('orders'); fetchOrders(); }}>Orders</button>
                </>
              )}
            </div>
          </div>

          <div className="mstore-ship">Small-batch mustards, shipped to your door · <b>Boxes ship free</b> · add-on jars from $8.49</div>

          <div className="mstore-body">
            {view === 'browse' && renderBrowse()}
            {view === 'origins' && renderOrigins()}
            {view === 'product' && renderProduct()}
            {view === 'cart' && renderCart()}
            {view === 'checkout' && renderCheckout()}
            {view === 'orders' && renderOrders()}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

export default StoreModal;
